import { useId, useMemo, useState, type ReactNode } from 'react';
import type { Can, Category } from '../../shared/api';
import { searchCans } from '../../shared/search';
import { CanImage } from './CanImage';

interface Props {
  cans: Can[];
  categories: Category[];
  onPick: (can: Can) => void;
  placeholder?: string;
  label?: string;
  initialQuery?: string;
  autoFocus?: boolean;
  /** Short status after the category, e.g. "✓" for flavors on the wall. */
  meta?: (can: Can) => string | null;
  /** Shown while the field is empty. */
  empty?: ReactNode;
  /** Shown instead of the plain "nothing found" line. */
  notFound?: ReactNode;
  limit?: number;
  /** Extra class for the result list (the scanner lets it fill the view). */
  listClassName?: string;
}

/** Autocomplete over the whole catalog: type a few letters, tap the can. */
export function CanSearch({
  cans,
  categories,
  onPick,
  placeholder = 'Zoek op naam of reeks',
  label = 'Zoek een blik',
  initialQuery = '',
  autoFocus,
  meta,
  empty,
  notFound,
  limit = 40,
  listClassName = '',
}: Props) {
  const [query, setQuery] = useState(initialQuery);
  const listId = useId();
  const catName = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories]);
  const results = useMemo(() => searchCans(cans, categories, query, limit), [cans, categories, query, limit]);
  const typed = query.trim().length > 0;
  const pick = (can: Can) => {
    setQuery('');
    onPick(can);
  };

  return (
    <div className="can-search">
      <input
        className="input"
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        autoFocus={autoFocus}
        data-autofocus={autoFocus ? '' : undefined}
        placeholder={placeholder}
        aria-label={label}
        aria-controls={listId}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && results[0]) {
            e.preventDefault();
            pick(results[0]);
          }
        }}
      />
      <div id={listId} className={`pick-list no-scrollbar ${listClassName}`} role="list">
        {results.map((c) => {
          const status = meta?.(c);
          return (
            <button key={c.id} type="button" role="listitem" className="pick-row" onClick={() => pick(c)}>
              <span className="pick-row__thumb">
                <CanImage imageId={c.imageId} empty="blank" />
              </span>
              <span className="pick-row__name">{c.name}</span>
              <span className="pick-row__cat">
                {catName.get(c.categoryId)}
                {status ? ` · ${status}` : ''}
              </span>
            </button>
          );
        })}
        {typed &&
          results.length === 0 &&
          (notFound ?? <div className="pick-list__empty">Geen blik gevonden met die naam.</div>)}
        {!typed && empty}
      </div>
    </div>
  );
}
